import React, { useState } from 'react';
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faPlus, faTrash } from "@fortawesome/free-solid-svg-icons";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import ConfirmDialog from "../../common/Modals/ConfirmDialog";

export default function EpkWaitlist({ epkInfo, isEditMode, isDirty, onChange }) {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const [showDiscardModal, setShowDiscardModal] = useState(false);

  // Guard config to default if undefined
  const config = epkInfo?.waitlistConfig || {
    enabled: false,
    closesAt: null,
    tiers: []
  };

  const enabled = config.enabled || false;
  // closesAt is a string from db, we need it in datetime-local format
  // A datetime-local input reads and writes wall-clock time in the viewer's zone.
  // toISOString() renders UTC, so a time entered locally used to redisplay shifted
  // by the UTC offset (the onChange side was already correct).
  const formatDatetimeLocal = (dateStr) => {
    if (!dateStr) return "";
    const d = new Date(dateStr);
    if (isNaN(d.getTime())) return "";
    const pad = (n) => String(n).padStart(2, "0");
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
  };
  const closesAt = config.closesAt ? formatDatetimeLocal(config.closesAt) : "";
  const tiers = config.tiers || [];

  if (!isEditMode) return null;

  const handleChange = (field, value) => {
    onChange && onChange('waitlistConfig', {
      ...config,
      [field]: value
    });
  };

  const handleAddTier = () => {
    const newTiers = [
      ...tiers,
      { basis: "referrals", threshold: 0, rewardLabel: "", rewardDetail: "" },
    ];
    handleChange('tiers', newTiers);
  };

  const handleRemoveTier = (index) => {
    const newTiers = [...tiers];
    newTiers.splice(index, 1);
    handleChange('tiers', newTiers);
  };

  const handleTierChange = (index, field, value) => {
    const newTiers = [...tiers];
    newTiers[index] = {
      ...newTiers[index],
      [field]: field === "threshold" ? (value === "" ? "" : Number(value)) : value,
    };
    handleChange('tiers', newTiers);
  };

  // The old Dashboard-only panel blocked its own save on half-filled tiers. This
  // section saves through the page's SAVE AND EXIT, so flag the bad fields inline
  // instead -- otherwise a blank threshold/label saves silently as 0 / "".
  const tierErrors = tiers.map((tier) => ({
    threshold: tier.threshold === "" || tier.threshold === null || Number(tier.threshold) < 0,
    rewardLabel: !String(tier.rewardLabel || "").trim(),
  }));
  const hasTierErrors = tierErrors.some((e) => e.threshold || e.rewardLabel);

  // Leaving the editor drops the in-progress draft, so only warn when there is
  // actually something to lose. Deep-links straight to this EPK's entries panel.
  const goToDashboard = () => {
    setShowDiscardModal(false);
    navigate(`/dashboard/epks?waitlist=${epkInfo._id}`);
  };

  const handleDashboardClick = () => {
    if (isDirty) {
      setShowDiscardModal(true);
      return;
    }
    goToDashboard();
  };

  const confirmLeave = goToDashboard;

  return (
    <div className="tw-py-8 tw-mb-8 tw-bg-[#1E0039] tw-w-full">
      <div className="tw-relative tw-w-full tw-max-w-[1232px] tw-mx-auto tw-flex tw-flex-col tw-px-4 md:tw-px-0">
        <div className="tw-flex tw-flex-col tw-mb-8 md:tw-mb-10">
          <span className="tw-text-[#FF43A7] tw-text-[10px] tw-font-bold tw-uppercase tw-tracking-widest tw-mb-1">
            {t("Waitlist")}
          </span>
          <h2 className="tw-text-white tw-text-3xl md:tw-text-4xl tw-font-bold tw-tracking-tight tw-mb-2">
            {t("Edit Waitlist")}
          </h2>
          <p className="tw-text-[#DDB7FF] tw-text-sm md:tw-text-base tw-leading-relaxed tw-max-w-[672px] tw-mt-2 tw-mb-0">
            {t("Configure your waitlist campaign settings. The waitlist allows fans to join, refer friends, and earn rewards based on their position or referrals.")}
          </p>
        </div>

        <div className="tw-bg-[#280D41] tw-border tw-border-[#712CB0]/30 tw-w-full tw-rounded-[32px] md:tw-rounded-[40px] tw-py-8 md:tw-py-12 tw-px-4 md:tw-px-12 tw-text-white tw-space-y-8">
          
          <div className="tw-flex tw-flex-col sm:tw-flex-row sm:tw-items-center tw-justify-between tw-gap-4 tw-bg-[#1E0039] tw-border tw-border-[#712CB0]/30 tw-p-6 tw-rounded-2xl">
            <div className="tw-flex tw-items-center tw-gap-4">
              <input
                type="checkbox"
                id="enable-waitlist"
                checked={enabled}
                onChange={(e) => handleChange("enabled", e.target.checked)}
                className="tw-h-5 tw-w-5 tw-rounded tw-border-[#5A3F49] tw-text-[#FF43A7] focus:tw-ring-[#FF43A7]"
              />
              <label htmlFor="enable-waitlist" className="tw-font-bold tw-text-white tw-select-none">
                {t("Enable Join Waitlist Action")}
              </label>
            </div>
            
            <div className="tw-flex tw-flex-col tw-items-end tw-gap-2">
              <button
                type="button"
                onClick={handleDashboardClick}
                className="tw-bg-[#FF43A7] hover:tw-bg-[#e03290] tw-text-white tw-px-5 tw-py-2 tw-rounded-lg tw-text-xs tw-font-bold tw-uppercase tw-tracking-widest tw-border-none tw-cursor-pointer tw-transition-colors"
              >
                {t("Open in Dashboard")}
              </button>
              <span className="tw-text-[#DDB7FF] tw-text-[10px]">
                {t("Viewing and exporting who joined stays in your Dashboard.")}
              </span>
            </div>
          </div>

          <div className="tw-max-w-md">
            <label className="tw-block tw-text-[#FF43A7] tw-text-xs tw-font-bold tw-uppercase tw-tracking-widest tw-mb-2">
              {t("Scheduled Close Datetime")} <span className="tw-text-[#DDB7FF]">({t("Optional")})</span>
            </label>
            <input
              type="datetime-local"
              value={closesAt}
              onChange={(e) => handleChange("closesAt", e.target.value ? new Date(e.target.value).toISOString() : null)}
              className="tw-w-full tw-bg-[#1E0039] tw-border tw-border-[#5A3F49] tw-rounded-lg tw-px-4 tw-py-3 tw-text-white focus:tw-outline-none focus:tw-border-[#FF43A7] tw-transition-colors"
            />
            <span className="tw-block tw-text-[#DDB7FF] tw-text-xs tw-mt-2">
              {t("Leave blank to run indefinitely until manual close.")}
            </span>
          </div>

          <div className="tw-space-y-4">
            <div className="tw-flex tw-items-center tw-justify-between">
              <h3 className="tw-m-0 tw-text-[#FF43A7] tw-text-xs tw-font-bold tw-uppercase tw-tracking-widest">
                {t("Configure Reward Tiers")}
              </h3>
              <button
                type="button"
                onClick={handleAddTier}
                className="tw-flex tw-items-center tw-gap-2 tw-bg-transparent hover:tw-bg-white/5 tw-border tw-border-[#FF43A7] tw-text-[#FF43A7] tw-px-4 tw-py-2 tw-rounded-lg tw-text-xs tw-font-bold tw-uppercase tw-cursor-pointer tw-transition-colors"
              >
                <FontAwesomeIcon icon={faPlus} />
                {t("Add Tier")}
              </button>
            </div>

            {tiers.length === 0 ? (
              <p className="tw-text-sm tw-text-[#DDB7FF] tw-bg-[#1E0039] tw-border tw-border-[#712CB0]/30 tw-p-6 tw-rounded-2xl tw-text-center tw-m-0">
                {t("No reward tiers configured yet. Add rewards to incentivize referrals!")}
              </p>
            ) : (
              <div className="tw-space-y-4">
                {tiers.map((tier, index) => (
                  <div
                    key={index}
                    className="tw-flex tw-flex-col lg:tw-flex-row tw-items-start lg:tw-items-center tw-gap-4 tw-bg-[#1E0039] tw-border tw-border-[#712CB0]/30 tw-p-6 tw-rounded-2xl tw-relative"
                  >
                    <div className="tw-w-full lg:tw-w-48">
                      <label className="tw-block tw-text-[#FF43A7] tw-text-[10px] tw-font-bold tw-uppercase tw-tracking-widest tw-mb-2">
                        {t("Basis")}
                      </label>
                      <select
                        value={tier.basis}
                        onChange={(e) => handleTierChange(index, "basis", e.target.value)}
                        className="tw-w-full tw-bg-[#280D41] tw-border tw-border-[#5A3F49] tw-rounded-lg tw-px-3 tw-py-2 tw-text-white focus:tw-outline-none focus:tw-border-[#FF43A7]"
                      >
                        <option value="referrals">{t("Referrals")}</option>
                        <option value="position">{t("Position")}</option>
                      </select>
                    </div>

                    <div className="tw-w-full lg:tw-w-40">
                      <label className="tw-block tw-text-[#FF43A7] tw-text-[10px] tw-font-bold tw-uppercase tw-tracking-widest tw-mb-2">
                        {tier.basis === "referrals" ? t("Min Referrals") : t("Max Position")}
                      </label>
                      <input
                        type="number"
                        min="0"
                        value={tier.threshold}
                        onChange={(e) => handleTierChange(index, "threshold", e.target.value)}
                        className={`tw-w-full tw-bg-[#280D41] tw-border tw-rounded-lg tw-px-3 tw-py-2 tw-text-white focus:tw-outline-none focus:tw-border-[#FF43A7] ${tierErrors[index]?.threshold ? "tw-border-red-500" : "tw-border-[#5A3F49]"}`}
                        placeholder="0"
                      />
                    </div>

                    <div className="tw-flex-1 tw-w-full">
                      <label className="tw-block tw-text-[#FF43A7] tw-text-[10px] tw-font-bold tw-uppercase tw-tracking-widest tw-mb-2">
                        {t("Reward Title")}
                      </label>
                      <input
                        type="text"
                        value={tier.rewardLabel}
                        onChange={(e) => handleTierChange(index, "rewardLabel", e.target.value)}
                        className={`tw-w-full tw-bg-[#280D41] tw-border tw-rounded-lg tw-px-3 tw-py-2 tw-text-white focus:tw-outline-none focus:tw-border-[#FF43A7] ${tierErrors[index]?.rewardLabel ? "tw-border-red-500" : "tw-border-[#5A3F49]"}`}
                        placeholder={t("e.g. VIP Screening Access")}
                      />
                    </div>

                    <div className="tw-flex-1 tw-w-full">
                      <label className="tw-block tw-text-[#FF43A7] tw-text-[10px] tw-font-bold tw-uppercase tw-tracking-widest tw-mb-2">
                        {t("Description")}
                      </label>
                      <input
                        type="text"
                        value={tier.rewardDetail}
                        onChange={(e) => handleTierChange(index, "rewardDetail", e.target.value)}
                        className="tw-w-full tw-bg-[#280D41] tw-border tw-border-[#5A3F49] tw-rounded-lg tw-px-3 tw-py-2 tw-text-white focus:tw-outline-none focus:tw-border-[#FF43A7]"
                        placeholder={t("e.g. Receive a free ticket to the opening night")}
                      />
                    </div>

                    <button
                      type="button"
                      onClick={() => handleRemoveTier(index)}
                      className="tw-bg-transparent tw-border-none tw-text-[#DDB7FF] hover:tw-text-red-500 tw-cursor-pointer tw-p-2 lg:tw-mt-6 tw-transition-colors"
                    >
                      <FontAwesomeIcon icon={faTrash} />
                    </button>
                  </div>
                ))}
              </div>
            )}

            {hasTierErrors && (
              <p className="tw-m-0 tw-text-red-400 tw-text-xs">
                {t("Please fill out all reward tier fields with valid values")}
              </p>
            )}
          </div>
        </div>
      </div>

      <ConfirmDialog
        isOpen={showDiscardModal}
        variant="discard"
        title={t("Discard Changes?")}
        confirmLabel={t("Leave without saving")}
        cancelLabel={t("Keep Editing")}
        onConfirm={confirmLeave}
        onCancel={() => setShowDiscardModal(false)}
      >
        {t("You have unsaved changes. Are you sure you want to discard them? All your progress will be lost.")}
      </ConfirmDialog>
    </div>
  );
}
